import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Mail, Pencil, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ImportDialog from "../components/ImportDialog";

export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", phone: "", email: "", npwp: "", address: "" });
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", contact: "", phone: "", email: "", npwp: "", address: "" }); setEditingId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, contact: c.contact || "", phone: c.phone || "", email: c.email || "", npwp: c.npwp || "", address: c.address || "" }); setEditingId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Nama customer wajib");
    try {
      if (editingId) await api.put(`/customers/${editingId}`, form);
      else await api.post("/customers", form);
      toast.success(editingId ? "Customer diperbarui" : "Customer ditambahkan");
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus customer ini?")) return;
    await api.delete(`/customers/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Direktori</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Customer</h1>
          <p className="text-sm text-slate-500 mt-1">Customer dengan email akan otomatis menerima invoice via email.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="customer-import-btn"><Upload size={14} className="mr-1" />Import Excel</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="customer-add-btn"><Plus size={16} className="mr-2" />Tambah Customer</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit Customer" : "Customer Baru"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nama</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="customer-email-input" type="email" placeholder="customer@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Nama PIC (Attn) <span className="text-xs text-slate-400 font-normal">tercetak di Invoice/DO</span></Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Cth: Mr. Naim" data-testid="customer-pic-input" /></div>
              <div><Label>NPWP <span className="text-xs text-slate-400 font-normal">(untuk e-Faktur DJP)</span></Label><Input placeholder="00.000.000.0-000.000" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} data-testid="customer-npwp-input" /></div>
              <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Alamat</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="customer-save-btn">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Card className="card-elevated"><CardContent className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>NPWP</TableHead><TableHead>PIC (Attn)</TableHead><TableHead>Telepon</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-sm">{s.email ? <span className="inline-flex items-center gap-1 text-slate-700"><Mail size={12} className="text-yellow-600" />{s.email}</span> : <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="font-mono text-xs">{s.npwp || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell>{s.contact}</TableCell>
                <TableCell className="font-mono text-xs">{s.phone}</TableCell>
                <TableCell>{user.role === "admin" || user.role === "staff_sales" ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`customer-edit-${s.id}`}><Pencil size={14} /></Button>
                    {user.role === "admin" && <Button size="sm" variant="ghost" onClick={() => remove(s.id)}><Trash2 size={14} className="text-red-500" /></Button>}
                  </>
                ) : null}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Belum ada customer</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
      <ImportDialog
        open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load}
        title="Import Customer dari Excel/CSV"
        endpoint="/customers/import"
        templateName="template_customer"
        columns={[
          { key: "name", label: "Nama", required: true, type: "string" },
          { key: "email", label: "Email", type: "string" },
          { key: "contact", label: "PIC (Attn)", type: "string" },
          { key: "npwp", label: "NPWP", type: "string" },
          { key: "phone", label: "Telepon", type: "string" },
          { key: "address", label: "Alamat", type: "string" },
        ]}
        sampleRow={{ name: "PT Contoh Sejahtera", email: "info@contoh.co.id", contact: "Budi", npwp: "01.234.567.8-901.000", phone: "021-1234567", address: "Jakarta" }}
      />
    </div>
  );
}
